<template>
  <b-modal
    id="page-tabs-modal"
    :ok-title="trans(block && block.id ? 'save' : 'create')"
    :cancel-title="trans('cancel')"
    size="lg"
    no-enforce-focus
    @shown="onShown"
    @ok="onSave"
  >
    <template v-slot:modal-title>
      <span>{{ trans('tabs') }}</span>
      <small v-if="!!block" class="text-muted ml-2">
        #{{ block.id }}
      </small>
    </template>
    
    <b-tabs
      :content-class="vertical ? 'w-50' : ''"
      :pills="pills" 
      :vertical="vertical"
      v-model="tab_index"
    >
      <b-tab v-for="(tab, index) in tabs" 
        class="mb-3" 
        :key="index"
        :title="tab.title"
      >
        <b-row :class="vertical ? 'mx-0 pb-3' : 'mx-0 py-3'">
          <b-button
            class="py-0"
            variant="outline-secondary"
            size="sm"
            @click="onRename(index)"
          > 
            <i class="fa fa-solid fa-pen"></i>
            {{ trans('rename') }}
          </b-button>
          <b-button 
            class="py-0 ml-4"
            variant="outline-secondary"
            size="sm"
            @click="tabs.splice(index, 1)"
          > 
            <i class="fa fa-fw fa-times"></i>
            {{ trans('remove_tab') }}
          </b-button>
        </b-row>
        <html-textarea
          :organization="organization"
          v-model="tab.text"
          for-microsite
          show-insert-form-document
          showHtmlMode
          @html="onHtmlMode(tab, $event)"
          :html_mode_value="tab.htmlModeValue"
        ></html-textarea>
      </b-tab>

      <template #tabs-end>
        <b-nav-item role="presentation" @click.prevent="onAdd" href="#">
          <i class="fa fa-fw fa-plus"></i>
          {{ trans('add') }}
        </b-nav-item>
      </template>
    </b-tabs>

    <hr>

    <b-row class="mx-0">
      <b-form-checkbox switch v-model="pills" :disabled="busy">
        {{ trans('pills') }}
      </b-form-checkbox>
      <b-form-checkbox switch v-model="vertical" :disabled="busy" class="ml-4">
        {{ trans('vertical') }}
      </b-form-checkbox>
      <b-button variant="outline-secondary" size="sm" class="py-0 ml-4" @click="onOrder">
        <i class="fa fa-fw fa-list"></i>
        {{ trans('reorder') }}
      </b-button>
    </b-row>

    <hr>

    <b-form-group :label="trans('width')">
      <b-form-radio-group v-model="image_percent" :options="image_percent_options" />
    </b-form-group>
    <b-form-group :label="trans('position')">
      <b-form-radio-group v-model="span_row">
        <b-form-radio :value="false">
          {{ trans('inline') }}
        </b-form-radio>
        <b-form-radio :value="true">
          {{ trans('span_row') }}
        </b-form-radio>
      </b-form-radio-group>
    </b-form-group>
    <b-form-group v-if="span_row" :label="trans('align')">
      <b-form-radio-group v-model="h_align" :options="h_align_options"></b-form-radio-group>
    </b-form-group>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="error" class="text-danger">
      {{ error }}
    </p>

    <TextareaModal
      id="tab-title-input-modal"
      type="input"
      :title="trans('tab_title')"
      :label="trans('title')"
      @input="onAddedOrRenamed"
      v-model="name"
    />

    <ReorderModal
      id="tabs-order-modal"
      nameAttribute="title"
      :propItems="tabs"
      @saved="onReordered"
    />

  </b-modal>
</template>
  
<script>
  import TextareaModal from '../../../common/components/TextareaModal.vue'
  import HtmlTextarea from './../../../common/components/HtmlTextarea'
  import ReorderModal from './../../../common/components/ReorderModal'

  export default {
    props: {
      organization: Object,
      page: Object,
      block: Object,
      after: Number,
    },
    components: {
      HtmlTextarea,
      ReorderModal,
      TextareaModal
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        tab_index: 0,
        name: null,
        index: null,
        tabs: [],
        pills: false,
        vertical: false,
        image_percent: 1.0,
        image_percent_options: [
          {text: '25%', value: 0.25},
          {text: '33%', value: 0.3333},
          {text: '50%', value: 0.5},
          {text: '66%', value: 0.6666},
          {text: '75%', value: 0.75},
          {text: '100%', value: 1.0},
        ],
        span_row: true,
        h_align: 'left',
        h_align_options: [
          {value: 'left', text: trans('left')},
          {value: 'center', text: trans('center')},
          {value: 'right', text: trans('right')},
        ],
      }
    },
    computed: {
      serialized() {
        return {
          type: 'tabs',
          text: this.text,
          after: this.after,
          meta: {
            tabs: this.tabs,
            pills: this.pills,
            vertical: this.vertical,
          },
          image_percent: this.image_percent,
          span_row: this.span_row,
          h_align: this.h_align,
        }
      },
    },
    methods: {
      onShown() {
        this.busy = false
        this.error = null
        this.text = this.block ? this.block.text : null
        this.tabs = this.block && this.block.meta && _.cloneDeep(this.block.meta.tabs) || [{title: trans('new_tab'), text: ''}]
        this.pills = this.block && this.block.meta && this.block.meta.pills || false
        this.vertical = this.block && this.block.meta && this.block.meta.vertical || false
        this.image_percent = this.block ? this.block.image_percent : 1.0
        this.span_row = this.block ? this.block.span_row || true : true
        this.h_align = this.block ? this.block.h_align || 'left' : 'left'
      },
      onSave(e) {
        e.preventDefault()
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/page/${this.page.id}/block`
        if (this.block && this.block.id) url += `/${this.block.id}`
        axios[this.block && this.block.id ? 'patch' : 'post'](url, this.serialized)
        .then(response => {
          this.busy = false
          this.$emit('saved', response.data)
          this.$bvModal.hide('page-tabs-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onHtmlMode(tab, value) {
        tab.htmlModeValue = value
      },
      onReordered(items) {
        let newItems = []
        items.map((i) => {
          newItems.push(i.item)
        })
        this.tabs = newItems
      },
      onOrder() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$bvModal.show('tabs-order-modal')
          }, 500) // whyyyy
        })
      },
      onAdd() {
        this.index = null
        this.name = trans('new_tab')
        this.$bvModal.show('tab-title-input-modal')
      },
      onRename(index) {
        this.index = index
        this.name = this.tabs[index].title
        this.$bvModal.show('tab-title-input-modal')
      },
      onAddedOrRenamed(description) {
        if (this.index === null) {
          var newTab = { title: description, text: '' };
          this.tabs.push(newTab);

          //update the tab index to activate the new tab
          this.$nextTick(() => {
            setTimeout(() => {
              this.tab_index = this.tabs.length - 1;
            }, 100) // is it the lifecycle of b-tabs fault?
          })

        } else {
          let tab = this.tabs[this.index];
          tab.title = description;
          this.$set(this.tabs, this.index, tab);
        }
      },
    },
    mounted() {
      window.pageTabsModal = this
    },
  }
</script>